import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Users,
  Search,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Store,
  UserCheck,
  Banknote,
  Target,
  RefreshCw,
  BarChart2,
  Calendar,
  X,
} from 'lucide-react';

/** Placeholders until BigQuery-backed API is wired */
const CUSTOMERS = [];
const SEGMENT_SUMMARY = [
  { key: 'ロイヤル顧客', label: 'ロイヤル顧客', icon: '👑', description: '高単価・高頻度の優良顧客', count: 0 },
  { key: 'ミドル顧客', label: 'ミドル顧客', icon: '⭐', description: '安定的に来店する顧客', count: 0 },
  { key: 'ライト顧客', label: 'ライト顧客', icon: '💡', description: '来店はあるが伸びしろあり', count: 0 },
  { key: '初回顧客', label: '初回顧客', icon: '🌱', description: '初回来店の顧客', count: 0 },
  { key: '離反リスク客', label: '離反リスク客', icon: '⚠️', description: '来店間隔が空き始めた顧客', count: 0 },
  { key: '離脱客', label: '離脱客', icon: '📤', description: '長期未来店の顧客', count: 0 },
];
const CUSTOMER_DETAIL = {
  history: [],
  firstVisitDate: '—',
  avgUnitPrice: 0,
};

const SEGMENT_COLORS = {
  ロイヤル顧客: 'bg-[#33564B] text-white',
  ミドル顧客: 'border border-[#33564B] text-[#33564B] bg-transparent',
  ライト顧客: 'border border-sky-400 text-sky-500 bg-transparent',
  初回顧客: 'bg-purple-500 text-white',
  離反リスク客: 'border border-orange-400 text-orange-500 bg-transparent',
  離脱客: 'border border-pink-400 text-pink-500 bg-transparent',
};

function SegmentBadge({ segment }) {
  const cls = SEGMENT_COLORS[segment] || 'border border-gray-300 text-gray-600 bg-transparent';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
      {segment}
    </span>
  );
}

function AppointmentBadge({ type }) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold mr-1 ${
        type === '指名' ? 'bg-[#33564B] text-white' : 'border border-gray-400 text-gray-500'
      }`}
    >
      {type}
    </span>
  );
}

function VisitTypeBadge({ type }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
        type === '施術' ? 'bg-purple-500 text-white' : 'bg-orange-400 text-white'
      }`}
    >
      {type}
    </span>
  );
}

function CustomerDetailModal({ customer, onClose }) {
  if (!customer) return null;

  const kpiCards = [
    { icon: <Store className="w-5 h-5" />, label: '最終利用店舗', value: customer.lastStore },
    { icon: <UserCheck className="w-5 h-5" />, label: '最終担当者', value: customer.lastStylist },
    { icon: <Banknote className="w-5 h-5" />, label: '累計売上', value: `¥${customer.totalAmount.toLocaleString()}` },
    { icon: <Target className="w-5 h-5" />, label: '累計来店回数', value: `${customer.visitCount}回` },
    { icon: <BarChart2 className="w-5 h-5" />, label: '平均単価', value: `¥${customer.avgUnitPrice.toLocaleString()}` },
    { icon: <RefreshCw className="w-5 h-5" />, label: '平均来店期間（ヵ月）', value: String(customer.avgVisitInterval) },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div
        className="bg-white rounded-xl w-full max-h-[90vh] overflow-y-auto"
        style={{ maxWidth: '900px', width: '95vw' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="p-6">
          <div className="flex justify-end mb-2">
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="閉じる">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-56 shrink-0 min-w-0">
              <p className="text-xs text-gray-400 mb-1">カルテ番号　{customer.karteNo}</p>
              <h2 className="text-2xl font-bold text-gray-800 mb-0.5">{customer.name}</h2>
              <p className="text-sm text-gray-500 mb-2">{customer.nameKana}</p>
              <SegmentBadge segment={customer.segment} />
              <p className="text-xs text-gray-500 mt-4">初回来店日　{customer.firstVisitDate}</p>
            </div>

            <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-3">
              {kpiCards.map((card) => (
                <div key={card.label} className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#33564B] flex items-center justify-center text-white shrink-0">{card.icon}</div>
                    <span className="text-xs text-gray-500">{card.label}</span>
                  </div>
                  <p className="text-base font-semibold text-gray-800 pl-10">{card.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 border border-gray-200 rounded-xl overflow-hidden">
            <div className="border-b border-gray-200 px-4 pt-4 pb-0">
              <span className="text-sm font-semibold text-gray-700 border-b-2 border-[#3F5B3B] pb-2 inline-block">利用履歴</span>
            </div>
            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {customer.history.map((h) => (
                <div
                  key={h.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 text-sm"
                >
                  <span className="w-24 text-gray-500 shrink-0">{h.date}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <VisitTypeBadge type={h.type} />
                    <span className="text-gray-700 whitespace-pre-line min-w-0 flex-1">{h.menu}</span>
                  </div>
                  <span className="w-36 text-gray-600 shrink-0 hidden md:inline">{h.store}</span>
                  <div className="w-32 shrink-0 flex flex-col gap-0.5">
                    <AppointmentBadge type={h.appointmentType} />
                    <span className="text-gray-700">{h.stylist}</span>
                  </div>
                  <span className="w-20 sm:ml-auto text-left sm:text-right text-gray-800 font-medium shrink-0">
                    ¥{h.amount.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const STORE_OPTIONS = ['Linoサロン2号店', 'Linoサロン代官山', 'Linoサロン表参道', 'Linoサロン自由が丘'];
const STYLIST_OPTIONS = ['佐藤 健', '新垣 結衣', 'ヘア次郎', '佐々木 蔵之介'];

function FilterPanel({ onClose, onSearch }) {
  const [store, setStore] = useState('');
  const [stylist, setStylist] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [minVisits, setMinVisits] = useState('');
  const [maxVisits, setMaxVisits] = useState('');
  const [firstVisitFrom, setFirstVisitFrom] = useState('');
  const [firstVisitTo, setFirstVisitTo] = useState('');
  const [lastVisitFrom, setLastVisitFrom] = useState('');
  const [lastVisitTo, setLastVisitTo] = useState('');

  const handleReset = () => {
    setStore('');
    setStylist('');
    setMinAmount('');
    setMaxAmount('');
    setMinVisits('');
    setMaxVisits('');
    setFirstVisitFrom('');
    setFirstVisitTo('');
    setLastVisitFrom('');
    setLastVisitTo('');
  };

  const handleSearch = () => {
    onSearch({
      store,
      stylist,
      minAmount,
      maxAmount,
      minVisits,
      maxVisits,
      firstVisitFrom,
      firstVisitTo,
      lastVisitFrom,
      lastVisitTo,
    });
    onClose();
  };

  return (
    <div className="absolute right-0 top-10 z-50 w-[min(20rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg p-4 space-y-4">
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1">店舗</p>
        <select
          value={store}
          onChange={(e) => setStore(e.target.value)}
          className="w-full h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none focus:ring-1 focus:ring-[#33564B]"
        >
          <option value="">店舗を選択</option>
          {STORE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1">担当スタイリスト</p>
        <select
          value={stylist}
          onChange={(e) => setStylist(e.target.value)}
          className="w-full h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none focus:ring-1 focus:ring-[#33564B]"
        >
          <option value="">スタイリストを選択</option>
          {STYLIST_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1">累計金額</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="最低金額"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
          />
          <span className="text-gray-400 shrink-0">〜</span>
          <input
            type="number"
            placeholder="最高金額"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
          />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1">累計来店回数</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="最低回数"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={minVisits}
            onChange={(e) => setMinVisits(e.target.value)}
          />
          <span className="text-gray-400 shrink-0">〜</span>
          <input
            type="number"
            placeholder="最高回数"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={maxVisits}
            onChange={(e) => setMaxVisits(e.target.value)}
          />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1">初回来店日</p>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={firstVisitFrom}
            onChange={(e) => setFirstVisitFrom(e.target.value)}
          />
          <span className="text-gray-400 shrink-0">〜</span>
          <input
            type="date"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={firstVisitTo}
            onChange={(e) => setFirstVisitTo(e.target.value)}
          />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1">最終来店日</p>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={lastVisitFrom}
            onChange={(e) => setLastVisitFrom(e.target.value)}
          />
          <span className="text-gray-400 shrink-0">〜</span>
          <input
            type="date"
            className="flex-1 h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
            value={lastVisitTo}
            onChange={(e) => setLastVisitTo(e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={handleReset}
          className="flex-1 h-8 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
        >
          リセット
        </button>
        <button
          type="button"
          onClick={handleSearch}
          className="flex-1 h-8 text-xs bg-[#33564B] hover:bg-[#2a463d] text-white rounded-lg transition-colors"
        >
          検索
        </button>
      </div>
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const PERIOD_OPTIONS = [
  { value: 'all', label: 'すべて' },
  { value: '30d', label: '過去30日間' },
  { value: '3m', label: '過去3ヵ月' },
  { value: '6m', label: '過去6ヵ月' },
  { value: '12m', label: '過去12ヵ月' },
  { value: 'custom', label: '期間を指定' },
];

const SEGMENT_CARD_ACTIVE = {
  ロイヤル顧客: 'bg-[#33564B] border-[#33564B]',
  ミドル顧客: 'bg-[#3F5B3B] border-[#3F5B3B]',
  ライト顧客: 'bg-sky-500 border-sky-500',
  初回顧客: 'bg-purple-500 border-purple-500',
  離反リスク客: 'bg-orange-500 border-orange-500',
  離脱客: 'bg-pink-500 border-pink-500',
};

export default function CustomerAnalysisView() {
  const [keyword, setKeyword] = useState('');
  const [period, setPeriod] = useState('all');
  const [activeSegment, setActiveSegment] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState(null);
  const [filterLabel, setFilterLabel] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showPageSizeMenu, setShowPageSizeMenu] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const customDateRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customDateRef.current && !customDateRef.current.contains(e.target)) {
        setShowCustomDate(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getPeriodDateThreshold = () => {
    const now = new Date();
    if (period === '30d') {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d;
    }
    if (period === '3m') {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 3);
      return d;
    }
    if (period === '6m') {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 6);
      return d;
    }
    if (period === '12m') {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
    return null;
  };

  const filtered = useMemo(() => {
    let list = [...CUSTOMERS];
    if (keyword) {
      list = list.filter((c) => c.name.includes(keyword) || c.nameKana.includes(keyword) || c.karteNo.includes(keyword));
    }
    if (activeSegment) {
      list = list.filter((c) => c.segment === activeSegment);
    }
    if (period === 'custom') {
      if (customDateFrom) {
        const from = new Date(customDateFrom);
        list = list.filter((c) => new Date(c.lastVisitDate.replace(/\//g, '-')) >= from);
      }
      if (customDateTo) {
        const to = new Date(customDateTo);
        list = list.filter((c) => new Date(c.lastVisitDate.replace(/\//g, '-')) <= to);
      }
    } else {
      const threshold = getPeriodDateThreshold();
      if (threshold) {
        list = list.filter((c) => new Date(c.lastVisitDate.replace(/\//g, '-')) >= threshold);
      }
    }
    if (filters && filters.store) list = list.filter((c) => c.lastStore === filters.store);
    if (filters && filters.stylist) list = list.filter((c) => c.lastStylist === filters.stylist);
    if (filters && filters.minAmount) list = list.filter((c) => c.totalAmount >= Number(filters.minAmount));
    if (filters && filters.maxAmount) list = list.filter((c) => c.totalAmount <= Number(filters.maxAmount));
    return list;
  }, [keyword, activeSegment, filters, period, customDateFrom, customDateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSegmentClick = (seg) => {
    setActiveSegment((prev) => (prev === seg ? null : seg));
    setPage(1);
  };

  const handleSearch = (f) => {
    setFilters(f);
    const parts = [];
    if (f.store) parts.push(f.store);
    if (f.stylist) parts.push(f.stylist);
    setFilterLabel(parts.join('、'));
    setPage(1);
  };

  const openDetail = (customer) => {
    const avgUnitPrice =
      customer.visitCount > 0 ? Math.round(customer.totalAmount / customer.visitCount) : CUSTOMER_DETAIL.avgUnitPrice;
    setSelectedCustomer({
      ...CUSTOMER_DETAIL,
      id: customer.id,
      karteNo: customer.karteNo,
      name: customer.name,
      nameKana: customer.nameKana,
      segment: customer.segment,
      lastStore: customer.lastStore,
      lastStylist: customer.lastStylist,
      appointmentType: customer.appointmentType,
      lastVisitDate: customer.lastVisitDate,
      totalAmount: customer.totalAmount,
      visitCount: customer.visitCount,
      avgVisitInterval: customer.avgVisitInterval,
      avgUnitPrice,
      firstVisitDate: customer.firstVisitDate ?? CUSTOMER_DETAIL.firstVisitDate,
      history: customer.history ?? CUSTOMER_DETAIL.history,
    });
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-col h-full bg-[#E4E7E6] min-h-0 -m-6 p-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between px-3 md:px-6 py-3 bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <Users className="w-5 h-5 text-[#33564B]" />
          <h1 className="text-base font-bold text-gray-800 font-['Noto Sans']">顧客一覧</h1>
          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full">{filtered.length}名</span>
          <div className="relative" ref={customDateRef}>
            <select
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value);
                setPage(1);
                if (e.target.value === 'custom') setShowCustomDate(true);
                else setShowCustomDate(false);
              }}
              className="h-8 text-xs w-full max-w-[9rem] sm:w-36 border border-gray-200 rounded-lg px-2 focus:outline-none focus:ring-1 focus:ring-[#33564B] bg-white"
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {period === 'custom' && showCustomDate && (
              <div className="absolute left-0 top-10 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-72 space-y-3">
                <div className="flex items-center gap-1 text-xs font-semibold text-gray-700 mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  期間を指定
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">開始日</p>
                  <input
                    type="date"
                    className="w-full h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
                    value={customDateFrom}
                    onChange={(e) => {
                      setCustomDateFrom(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">終了日</p>
                  <input
                    type="date"
                    className="w-full h-8 text-xs border border-gray-200 rounded-lg px-2 focus:outline-none"
                    value={customDateTo}
                    onChange={(e) => {
                      setCustomDateTo(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    className="flex-1 h-7 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                    onClick={() => {
                      setCustomDateFrom('');
                      setCustomDateTo('');
                      setPage(1);
                    }}
                  >
                    リセット
                  </button>
                  <button
                    type="button"
                    className="flex-1 h-7 text-xs bg-[#33564B] hover:bg-[#2a463d] text-white rounded-lg"
                    onClick={() => setShowCustomDate(false)}
                  >
                    適用
                  </button>
                </div>
              </div>
            )}
            {period === 'custom' && (customDateFrom || customDateTo) && !showCustomDate && (
              <button
                type="button"
                className="absolute left-0 top-10 text-xs text-[#33564B] underline whitespace-nowrap"
                onClick={() => setShowCustomDate(true)}
              >
                {customDateFrom || ''} 〜 {customDateTo || ''}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto min-w-0">
          <div className="relative flex-1 min-w-0 md:flex-initial">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              className="pl-8 h-8 text-xs w-full md:w-52 min-w-0 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#33564B]"
              placeholder="フリーワード"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="relative shrink-0">
            <button
              type="button"
              className={`h-8 text-xs px-3 flex items-center justify-center gap-1.5 border rounded-lg transition-colors w-full sm:w-auto ${
                showFilter ? 'bg-gray-100 border-gray-300' : 'border-gray-200 bg-white hover:bg-gray-50'
              } text-gray-600`}
              onClick={() => setShowFilter((v) => !v)}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              絞り込み
            </button>
            {showFilter && <FilterPanel onClose={() => setShowFilter(false)} onSearch={handleSearch} />}
          </div>
        </div>
      </div>

      {filterLabel && (
        <div className="px-3 md:px-6 py-1.5 bg-white border-b border-gray-100 text-xs text-gray-500">絞り込み内容：{filterLabel}</div>
      )}

      <div className="flex-1 px-3 md:px-6 py-4 space-y-4 overflow-auto min-h-0">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {SEGMENT_SUMMARY.map((seg) => {
            const isActive = activeSegment === seg.key;
            return (
              <button
                key={seg.key}
                type="button"
                onClick={() => handleSegmentClick(seg.key)}
                className={`relative text-left p-3 rounded-xl border transition-all ${
                  isActive
                    ? `${SEGMENT_CARD_ACTIVE[seg.key]} text-white shadow-sm`
                    : 'border-gray-200 bg-white hover:border-[#33564B]/40 hover:shadow-sm'
                }`}
              >
                <div
                  className={`absolute top-2 right-2 w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                    isActive ? 'bg-white/30 border-white/60' : 'border-gray-300'
                  }`}
                >
                  {isActive && <span className="text-white text-[8px] font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-base">{seg.icon}</span>
                  <span className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-gray-700'}`}>{seg.label}</span>
                </div>
                <p className={`text-[10px] whitespace-pre-line leading-relaxed mb-2 ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                  {seg.description}
                </p>
                <span
                  className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {seg.count}名
                </span>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px] md:min-w-0">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 min-w-[7rem]">お客様名</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 min-w-[5.5rem]">顧客区分</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 min-w-[8rem]">
                    最終利用店舗
                  </th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 min-w-[9rem]">
                    最終担当者
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 min-w-[6rem]">
                    最終来店日 ▼
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 min-w-[5rem]">累計金額</th>
                  <th className="hidden md:table-cell text-right px-4 py-3 text-xs font-semibold text-gray-600 min-w-[4rem]">
                    累計
                    <br />
                    来店回数
                  </th>
                  <th className="hidden md:table-cell text-right px-4 py-3 text-xs font-semibold text-gray-600 min-w-[4.5rem]">
                    平均来店
                    <br />
                    期間(ヵ月)
                  </th>
                  <th className="hidden md:table-cell text-right px-4 py-3 text-xs font-semibold text-gray-600 min-w-[4.5rem]">
                    平均
                    <br />
                    客単価
                  </th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-600 min-w-[5.5rem]">
                    カルテ番号
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openDetail(customer)}
                        className="text-gray-800 font-medium text-sm hover:text-[#33564B] hover:underline transition-colors text-left font-['Noto Sans']"
                      >
                        {customer.name}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <SegmentBadge segment={customer.segment} />
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-gray-600 text-sm">{customer.lastStore}</td>
                    <td className="hidden md:table-cell px-4 py-3 text-gray-600 text-sm">
                      <AppointmentBadge type={customer.appointmentType} />
                      {customer.lastStylist}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{customer.lastVisitDate}</td>
                    <td className="px-4 py-3 text-right text-gray-800 text-sm">¥{customer.totalAmount.toLocaleString()}</td>
                    <td className="hidden md:table-cell px-4 py-3 text-right text-gray-600 text-sm">{customer.visitCount}回</td>
                    <td className="hidden md:table-cell px-4 py-3 text-right text-gray-600 text-sm">{customer.avgVisitInterval}</td>
                    <td className="hidden md:table-cell px-4 py-3 text-right text-gray-800 text-sm font-medium">
                      ¥{customer.visitCount > 0 ? Math.round(customer.totalAmount / customer.visitCount).toLocaleString() : 0}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-gray-500 text-xs">{customer.karteNo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 min-w-0">
              {filtered.length === 0
                ? '0件を表示'
                : `${filtered.length}中 ${(currentPage - 1) * pageSize + 1} 〜 ${Math.min(currentPage * pageSize, filtered.length)}件までを表示`}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-gray-50"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {getPageNumbers().map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="w-7 h-7 flex items-center justify-center text-xs text-gray-400">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-medium transition-colors ${
                      currentPage === p ? 'bg-[#33564B] text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 disabled:opacity-30 hover:bg-gray-50"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <div className="relative ml-4">
                <button
                  type="button"
                  onClick={() => setShowPageSizeMenu((v) => !v)}
                  className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50"
                >
                  表示する行数　{pageSize}
                  <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showPageSizeMenu && (
                  <div className="absolute bottom-8 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10">
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          setPageSize(size);
                          setPage(1);
                          setShowPageSizeMenu(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-xs hover:bg-gray-50 ${
                          pageSize === size ? 'bg-gray-100 font-semibold' : ''
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedCustomer && <CustomerDetailModal customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} />}
    </div>
  );
}
